import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Flex, Input, InputGroup, InputLeftElement, List, ListItem, Portal, Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSearch, FiPlus, FiExternalLink } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { NIL } from 'uuid';
import useItems from '../../hooks/useItems';
import { Item, isAvailableOn, DocumentAvailability } from '../../models/item';

// The "add item" search-select for the inline item editor (item 79): type to
// search the catalog (sku / brand / description), pick an existing item to drop a
// prefilled row, or "+ Add new …" to drop a blank editable row for a brand-new line.
interface CatalogComboboxProps {
  onPick: (item: Item) => void;
  onCreateNew: (typed: string) => void;
  vendorsOnly?: boolean;
  // item 39: only offer items available on this document type (omit = no filter)
  availableOn?: DocumentAvailability;
  // item 531 — INLINE SKU-cell mode. In an existing line's SKU cell the input SHOWS the
  // line's current sku, free-typing edits it live (via onTextChange), and picking a catalog
  // item swaps the whole line (onPick). No "Add new" row — the line already exists. When
  // `inline`, `value`/`onTextChange` are the controlled sku; otherwise the add-bar behaves
  // exactly as before with its own internal query state.
  inline?: boolean;
  value?: string;
  onTextChange?: (text: string) => void;
}

const CatalogCombobox: React.FC<CatalogComboboxProps> = ({
  onPick, onCreateNew, vendorsOnly, availableOn, inline, value, onTextChange,
}) => {
  const { data: items } = useItems();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // in inline mode the line's sku IS the query (controlled by the parent); in add mode
  // the internal query state drives both the input and the filter.
  const activeQuery = inline ? (value ?? '') : query;
  const setActiveQuery = (text: string) => {
    if (inline) onTextChange?.(text);
    else setQuery(text);
  };
  // item 460 — hardware carries far more SKUs than services; a kind filter +
  // grouped sections keep the picker findable as the catalog grows (Chad).
  const [kind, setKind] = useState<'all' | 'hardware' | 'services'>('all');
  const queryClient = useQueryClient();
  // item 879 — open the new-item form in a fresh tab; when the user comes back, refetch the
  // catalog ONCE so their just-created SKU shows up (global refetch-on-focus is off).
  const openNewItemTab = () => {
    window.open(`/item/${NIL}`, '_blank', 'noopener');
    const onFocus = () => {
      queryClient.invalidateQueries('items');
      window.removeEventListener('focus', onFocus);
    };
    window.addEventListener('focus', onFocus);
  };
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // item 528 — the dropdown is PORTALED to the body with fixed positioning so the slide-up
  // work-order panel's overflow can't clip it (Chad: the item picker was cut off / unusable).
  // Track the anchor's viewport rect to place the portaled list right under the input.
  const [rect, setRect] = useState<{
    left: number; width: number; top?: number; bottom?: number; openUp: boolean;
  } | null>(null);
  const reposition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // item 555 — flip the dropdown UP when there isn't room below (e.g. the last row near the
    // bottom of the page). Open up only when below is tight AND above has more room; otherwise
    // keep the natural downward direction. Uses `bottom` (viewport-anchored, fixed) to grow up.
    const DROP = 300; // ~maxH 280 + gap
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < DROP && r.top > spaceBelow;
    setRect({
      left: r.left,
      width: r.width,
      openUp,
      top: openUp ? undefined : r.bottom + 4,
      bottom: openUp ? window.innerHeight - r.top + 4 : undefined,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    reposition();
    // keep it aligned while the panel scrolls/resizes; close on a click outside both
    // the input and the portaled list (useOutsideClick can't see the portaled node).
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, reposition]);

  const menuBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const hover = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');

  const groups = useMemo(() => {
    const q = activeQuery.trim().toLowerCase();
    const all = items?.index ?? [];
    let pool = vendorsOnly ? all.filter((i) => i.vendorId) : all;
    // item 39: hide items not available on this document type
    if (availableOn !== undefined) pool = pool.filter((i) => isAvailableOn(i.availability, availableOn));
    if (q) pool = pool.filter((i) => `${i.sku} ${i.brand} ${i.description}`.toLowerCase().includes(q));
    // item 460 — grouped; item 1241 (Chad: "only shows 8 and search does not find all") —
    // the 8-per-section cap applies ONLY to the idle browse preview. The moment you TYPE,
    // every match renders (the menu scrolls) — search must never hide catalog items.
    const cap = q ? Number.POSITIVE_INFINITY : 8;
    const hardwareAll = kind === 'services' ? [] : pool.filter((i) => i.isHardware);
    const servicesAll = kind === 'hardware' ? [] : pool.filter((i) => !i.isHardware);
    const hardware = hardwareAll.slice(0, cap);
    const services = servicesAll.slice(0, cap);
    const hardwareMore = hardwareAll.length - hardware.length;
    const servicesMore = servicesAll.length - services.length;
    // item 1235 (Tom's dupe bug) — the availability filter HID a real catalog SKU
    // (AC-EX70-444-KIT imported without the Work Order bit), the picker showed nothing, and a
    // duplicate item got minted. A match the FILTER removed (never one removed by the search
    // text itself) now surfaces in its own "in the catalog, hidden here" section — picking it
    // uses the real item instead of breeding a copy. Availability keeps gating the browse
    // list; it just can't hide an item someone is literally typing the SKU of.
    let hidden: Item[] = [];
    if (q && availableOn !== undefined) {
      const shownIds = new Set([...hardware, ...services].map((i) => i.id));
      hidden = (vendorsOnly ? all.filter((i) => i.vendorId) : all)
        .filter((i) => !isAvailableOn(i.availability, availableOn))
        .filter((i) => `${i.sku} ${i.brand} ${i.description}`.toLowerCase().includes(q))
        .filter((i) => !shownIds.has(i.id))
        .slice(0, 5);
    }
    return {
      hardware,
      services,
      hidden,
      hardwareMore,
      servicesMore,
      empty: hardware.length + services.length + hidden.length === 0,
    };
  }, [items, activeQuery, vendorsOnly, availableOn, kind]);

  // inline: picking swaps the line, the parent owns the value — don't clear. add: reset.
  const pick = (item: Item) => { onPick(item); if (!inline) setQuery(''); setOpen(false); };
  const createNew = () => {
    onCreateNew(activeQuery.trim());
    if (!inline) setQuery('');
    setOpen(false);
  };

  return (
    <Box position="relative" ref={anchorRef}>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none" color={subColor}><FiSearch /></InputLeftElement>
        <Input
          placeholder={inline ? 'Search or type SKU…' : 'Search item or type to add new…'}
          value={activeQuery}
          variant="filled"
          borderRadius="lg"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setActiveQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            // inline: text already commits live via onTextChange; Enter just closes.
            if (e.key === 'Enter') {
              if (!inline && activeQuery.trim()) { e.preventDefault(); createNew(); } else setOpen(false);
            }
            if (e.key === 'Escape') setOpen(false);
          }}
        />
      </InputGroup>
      {open && rect && (
        <Portal>
          <List
            ref={listRef}
            position="fixed"
            {...(rect.openUp ? { bottom: `${rect.bottom}px` } : { top: `${rect.top}px` })}
            // item 879 (Chad: "this box is too small") — the dropdown gets a USABLE minimum
            // width regardless of how narrow its anchor input is (the à-la-carte wizard's SKU
            // cell), clamped so it never runs off the right edge of the viewport.
            left={`${Math.min(rect.left, Math.max(8, window.innerWidth - Math.max(rect.width, 380) - 8))}px`}
            width={`${Math.max(rect.width, 380)}px`}
            zIndex={2000}
            maxH="280px"
            overflowY="auto"
            bg={menuBg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="lg"
            boxShadow="xl"
            py={1}
          >
            {/* item 460 — kind filter chips: hardware has many more SKUs than services */}
            <ListItem px={2} pb={1}>
              <Flex>
                {(['all', 'hardware', 'services'] as const).map((k) => (
                  <Box
                    key={k}
                    as="button"
                    type="button"
                    px={2}
                    py={0.5}
                    mr={1}
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={kind === k ? 'brandGreen.400' : border}
                    color={kind === k ? 'brandGreen.400' : subColor}
                    onClick={(e: React.MouseEvent) => { e.preventDefault(); setKind(k); }}
                  >
                    {k}
                  </Box>
                ))}
              </Flex>
            </ListItem>
            {([['HARDWARE', groups.hardware, groups.hardwareMore], ['SERVICES', groups.services, groups.servicesMore]] as const)
              .filter(([, rows]) => rows.length > 0)
              .map(([label, rows, more]) => (
                <React.Fragment key={label}>
                  <ListItem px={3} pt={2} pb={1}>
                    <Text fontSize="0.65rem" fontWeight="bold" letterSpacing="0.08em" color={subColor}>
                      {`${label} (${rows.length})`}
                    </Text>
                  </ListItem>
                  {rows.map((item) => (
                    <ListItem
                      key={item.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: hover }}
                      onClick={() => pick(item)}
                    >
                      <Flex align="baseline" gap={2}>
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{item.sku || '(no sku)'}</Text>
                        <Text fontSize="xs" color={subColor} noOfLines={1}>{item.brand}</Text>
                        <Text fontSize="xs" color={subColor} ml="auto">{`$${item.retailEach}`}</Text>
                      </Flex>
                      {item.description && <Text fontSize="xs" color={subColor} noOfLines={1}>{item.description}</Text>}
                    </ListItem>
                  ))}
                  {/* item 1241 — the browse preview says what it's not showing */}
                  {more > 0 && (
                    <ListItem px={3} py={1}>
                      <Text fontSize="xs" color={subColor} fontStyle="italic">
                        {`+ ${more} more — type to search all of them`}
                      </Text>
                    </ListItem>
                  )}
                </React.Fragment>
              ))}
            {/* item 1235 — matches the availability filter would have hidden: visible, labeled,
                pickable — so an existing SKU can never be blindly re-created as a duplicate */}
            {groups.hidden.length > 0 && (
              <>
                <ListItem px={3} pt={2} pb={1}>
                  <Text fontSize="0.65rem" fontWeight="bold" letterSpacing="0.08em" color="orange.400">
                    {`IN THE CATALOG — NOT ENABLED FOR THIS DOCUMENT (${groups.hidden.length})`}
                  </Text>
                </ListItem>
                {groups.hidden.map((item) => (
                  <ListItem key={item.id} px={3} py={2} cursor="pointer" _hover={{ bg: hover }} onClick={() => pick(item)}>
                    <Flex align="baseline" gap={2}>
                      <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{item.sku || '(no sku)'}</Text>
                      <Text fontSize="xs" color={subColor} noOfLines={1}>{item.brand}</Text>
                      <Text fontSize="xs" color={subColor} ml="auto">{`$${item.retailEach}`}</Text>
                    </Flex>
                    <Text fontSize="xs" color="orange.400" noOfLines={1}>
                      Pick to use it here — fix its availability on the Items page.
                    </Text>
                  </ListItem>
                ))}
              </>
            )}
            {!inline && activeQuery.trim() && (
            <ListItem px={3} py={2} cursor="pointer" _hover={{ bg: hover }} onClick={createNew} color="brandGreen.400">
              {/* item 177 — explicit mr (Chakra-1.6 gap unreliable) */}
              <Flex align="center" fontSize="sm" fontWeight="semibold">
                <Box as={FiPlus} mr={2} flexShrink={0} />
                {`Add new "${activeQuery.trim()}"`}
              </Flex>
            </ListItem>
            )}
            {inline && groups.empty && (
            <ListItem px={3} py={2} fontSize="sm" color={subColor}>No catalog match — keep typing to set the SKU.</ListItem>
            )}
            {!inline && !activeQuery.trim() && groups.empty && (
            <ListItem px={3} py={2} fontSize="sm" color={subColor}>No catalog items — type to add one.</ListItem>
            )}
            {/* item 879 (Chad) — create a REAL catalog item from anywhere this picker lives:
                opens the Items form in a NEW TAB (this form/wizard stays put), and refreshes
                the catalog once focus returns so the new SKU is immediately searchable. */}
            <ListItem
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: hover }}
              borderTopWidth="1px"
              borderColor={border}
              onClick={openNewItemTab}
              color="brandGreen.400"
            >
              <Flex align="center" fontSize="sm" fontWeight="semibold">
                <Box as={FiExternalLink} mr={2} flexShrink={0} />
                Add to Items catalog — opens in a new tab
              </Flex>
            </ListItem>
          </List>
        </Portal>
      )}
    </Box>
  );
};

CatalogCombobox.defaultProps = {
  vendorsOnly: false, availableOn: undefined, inline: false, value: undefined, onTextChange: undefined,
};

export default CatalogCombobox;
